import type DateRange from "shared/helpers/DateRange";
import {
  CHARTS_WITH_INTERVAL,
  CHARTS_WITH_STACKED_HISTOGRAM,
} from "shared/modules/Charts/constants";
import type {
  ChartAxisInterval,
  ChartSetting,
} from "shared/modules/Charts/types";
import {
  CHART_AXIS_INTERVALS,
  ChartVariant,
} from "shared/modules/Charts/types";

export function useChartInterval() {
  function canUseChartInterval(setting: ChartSetting): boolean {
    if (!setting.chart || !CHARTS_WITH_INTERVAL.includes(setting.chart)) {
      return false;
    }

    if (setting.chart_variant === ChartVariant.Table) {
      return false;
    }

    if (CHARTS_WITH_STACKED_HISTOGRAM.includes(setting.chart)) {
      return [
        ChartVariant.StackedHorizontalBar,
        ChartVariant.StackedVerticalBar,
      ].includes(setting.chart_variant!);
    }

    return true;
  }

  const getIntervalPermissions = (
    range: DateRange
  ): Record<ChartAxisInterval, boolean> => {
    const { after, before } = range;

    const startDate = new Date(after * 1000);
    const endDate = new Date(before * 1000);

    const diffDays = Math.ceil(
      Math.abs(endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    const diffMonths =
      (endDate.getFullYear() - startDate.getFullYear()) * 12 +
      (endDate.getMonth() - startDate.getMonth());

    const isCompleteMonth =
      startDate.getDate() === 1 &&
      endDate.getDate() ===
        new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0).getDate();

    return {
      day: true,
      month: (diffMonths === 0 && isCompleteMonth) || diffDays >= 30,
      quarter: diffMonths >= 3 || diffDays >= 90,
      week: diffDays > 3,
      year: diffMonths >= 12 || diffDays >= 365,
    };
  };

  function getAllowedInterval(
    range: DateRange,
    selectedInterval?: ChartAxisInterval
  ): ChartAxisInterval {
    const intervalPermissions = getIntervalPermissions(range);

    const allowedIntervals = CHART_AXIS_INTERVALS.filter(
      (interval) => intervalPermissions[interval]
    );

    const currentIntervalAllowed =
      selectedInterval && allowedIntervals.includes(selectedInterval);

    if (currentIntervalAllowed) {
      return selectedInterval;
    }

    const lastAllowedInterval = allowedIntervals[0];

    return lastAllowedInterval;
  }

  return {
    canUseChartInterval,
    getAllowedInterval,
    getIntervalPermissions,
  };
}

export default useChartInterval;
